/* Ministry of Jesus — PWA client runtime.
 *
 * Public surface:
 *   • registerServiceWorker()  — call once at app boot.
 *   • onInstallAvailable(cb)   — subscribes to the beforeinstallprompt event
 *                                 so a React component can show an in-app
 *                                 install button on Android/desktop.
 *   • promptInstall()          — fires the stashed BIP event.
 *   • isStandalone()           — true when launched from Home Screen.
 *   • getPushState()           — { supported, permission, subscribed }.
 *   • subscribeToPush()        — asks for permission + registers the Push
 *                                 subscription with the backend.
 *   • unsubscribeFromPush()    — tears down and removes from backend.
 *
 * Notes:
 *   • We only register the SW in production builds OR when running on a
 *     real Emergent preview host — localhost dev would reload the SW on
 *     every HMR tick and churn caches.
 *   • Backend subscribe is same-origin via REACT_APP_BACKEND_URL + /api so
 *     cookies/credentials ride along.
 */

import api from '@/api/client';

const BIP_KEY = Symbol.for('moj.bip');
const listeners = new Set();

function isSupportedEnv() {
  if (typeof window === 'undefined') return false;
  if (!('serviceWorker' in navigator)) return false;
  // Avoid cluttering the dev-server devtools unless explicitly enabled.
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') {
    return window.location.search.includes('pwa=1');
  }
  return true;
}

// ---------- SW registration ----------
export async function registerServiceWorker() {
  if (!isSupportedEnv()) return null;
  try {
    const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    // Flush the offline queue the moment we have a live SW controller — this
    // covers the iOS case where Background Sync is unavailable.
    const ping = () => reg.active?.postMessage({ type: 'moj-flush-queue' });
    if (reg.active) ping();
    reg.addEventListener('updatefound', () => {
      const next = reg.installing;
      if (!next) return;
      next.addEventListener('statechange', () => {
        if (next.state === 'activated') ping();
      });
    });
    // Also flush when the browser detects we're back online.
    window.addEventListener('online', () => reg.active?.postMessage({ type: 'moj-flush-queue' }));
    return reg;
  } catch (e) {
    // Non-fatal — the site still works, just without offline support.
    // eslint-disable-next-line no-console
    console.warn('[MoJ] SW registration failed:', e);
    return null;
  }
}


// ---------- Install prompt ----------
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  window[BIP_KEY] = e;
  listeners.forEach((cb) => {
    try { cb(e); } catch (_) { /* ignore */ }
  });
});

window.addEventListener('appinstalled', () => {
  window[BIP_KEY] = null;
  listeners.forEach((cb) => {
    try { cb(null); } catch (_) { /* ignore */ }
  });
});

export function onInstallAvailable(cb) {
  listeners.add(cb);
  // Fire immediately if we already have a stashed prompt.
  if (window[BIP_KEY]) {
    try { cb(window[BIP_KEY]); } catch (_) { /* ignore */ }
  }
  return () => listeners.delete(cb);
}

export async function promptInstall() {
  const evt = window[BIP_KEY];
  if (!evt) return { outcome: 'unavailable' };
  evt.prompt();
  const choice = await evt.userChoice;
  window[BIP_KEY] = null;
  listeners.forEach((cb) => { try { cb(null); } catch (_) { /* ignore */ } });
  return choice;
}

export function isStandalone() {
  if (typeof window === 'undefined') return false;
  const navStandalone = 'standalone' in window.navigator && window.navigator.standalone;
  const matchDisplay = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
  return Boolean(navStandalone || matchDisplay);
}

export function isIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/i.test(ua) && !window.MSStream;
}


// ---------- Push notifications ----------
export function getPushSupport() {
  if (typeof window === 'undefined') return { supported: false };
  const supported = 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
  // iOS only supports Push when the PWA is installed to home screen (iOS 16.4+).
  const iosButNotInstalled = isIOS() && !isStandalone();
  return {
    supported: supported && !iosButNotInstalled,
    iosRequiresInstall: iosButNotInstalled,
    permission: typeof Notification !== 'undefined' ? Notification.permission : 'default',
  };
}

export async function getPushState() {
  const base = getPushSupport();
  if (!base.supported) return { ...base, subscribed: false };
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    return { ...base, subscribed: Boolean(sub), endpoint: sub?.endpoint || null };
  } catch (_) {
    return { ...base, subscribed: false };
  }
}

function base64UrlToUint8Array(value) {
  const padding = '='.repeat((4 - (value.length % 4)) % 4);
  const b64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  return Uint8Array.from([...raw], (c) => c.charCodeAt(0));
}

export async function subscribeToPush({ userId = null } = {}) {
  const state = getPushSupport();
  if (!state.supported) {
    const err = new Error(
      state.iosRequiresInstall
        ? 'On iPhone/iPad, add Ministry of Jesus to your Home Screen first, then open it from there to enable notifications.'
        : 'Push notifications are not supported in this browser.',
    );
    err.code = state.iosRequiresInstall ? 'IOS_INSTALL_REQUIRED' : 'UNSUPPORTED';
    throw err;
  }
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') {
    const err = new Error('Notification permission was not granted.');
    err.code = 'DENIED';
    throw err;
  }
  const reg = await navigator.serviceWorker.ready;
  const { data } = await api.get('/public/push/vapid-key');
  const publicKey = data?.publicKey;
  if (!publicKey) {
    const err = new Error('Server VAPID key unavailable.');
    err.code = 'NO_VAPID';
    throw err;
  }
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64UrlToUint8Array(publicKey),
    });
  }
  const json = sub.toJSON();
  const platform_hint = isIOS() ? 'ios' : /Android/i.test(navigator.userAgent) ? 'android' : 'desktop';
  await api.post('/public/push/subscribe', {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    user_id: userId,
    platform_hint,
  });
  return { endpoint: json.endpoint };
}

export async function unsubscribeFromPush() {
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const { endpoint } = sub;
  try { await sub.unsubscribe(); } catch (_) { /* ignore */ }
  try {
    await api.post('/public/push/unsubscribe', { endpoint });
  } catch (_) { /* backend will clean up on 410 anyway */ }
}


// ---------- Per-channel preferences (prayer pings, etc.) ----------
// The backend stores these alongside the subscription so toggling them never
// requires auth — the browser just needs to know its own endpoint.
export async function getPushPreferences() {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    if (!sub) return null;
    const { data } = await api.get('/public/push/preferences', {
      params: { endpoint: sub.endpoint },
    });
    return data;
  } catch (e) {
    if (e?.response?.status === 404) return null;
    throw e;
  }
}

export async function setPushPreferences(partial) {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  if (!sub) throw new Error('Not subscribed');
  await api.patch('/public/push/preferences', { endpoint: sub.endpoint, ...partial });
}
